import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { EmptyState } from '@/components/ui/empty-state';
import { useCanAdjustAssessment, useHealthReadinessQuery } from '@/lib/api/health-readiness';
import { CriteriaList, LIST_FRAME } from './rail';
import { CriterionPane } from './criterion-pane';
import { ManageCriteria } from './manage-criteria';
import { useHrNavigate } from './navigation';

/**
 * The Go-live criteria tab (T-0242 / E22-F02): the criteria by category on
 * the left, the selected criterion on the right. `/criteria` opens the
 * first criterion; `/criteria/<id>` that one (a stale id falls back to the
 * first). Leads open Manage criteria and weights from the list. A
 * deviation or deliverable in a criterion's evidence opens Contract
 * Compliance. Drawn in T-0243 (prototype `criteriaTab()`).
 */
export interface CriteriaTabProps {
  engagementId: string;
  criterionId?: string;
}

export function CriteriaTab({ engagementId, criterionId }: CriteriaTabProps) {
  const { data: view } = useHealthReadinessQuery(engagementId);
  const canManage = useCanAdjustAssessment();
  const go = useHrNavigate(engagementId);
  const navigate = useNavigate();
  const [manage, setManage] = useState(false);

  useEffect(() => setManage(false), [criterionId]);

  const detailRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = detailRef.current;
    if (el && el.getBoundingClientRect().top < 72) el.scrollIntoView?.({ block: 'start' });
  }, [criterionId, manage]);

  if (!view) return null;
  const readiness = view.verdict.readiness;
  if (!view.modules.goLiveReadiness || !readiness) {
    return (
      <EmptyState
        title="Go-live readiness is not in scope"
        description="Turn on Go-Live Readiness for this engagement to score go-live criteria."
      />
    );
  }
  const criterion = view.criteria.find((c) => c.id === criterionId) ?? view.criteria[0] ?? null;

  return (
    <div className="grid gap-4 lg:grid-cols-[300px_minmax(0,1fr)] lg:items-start">
      <aside className={LIST_FRAME}>
        <CriteriaList
          view={view}
          selected={criterion?.id ?? null}
          onSelect={(id) => {
            setManage(false);
            go({ tab: 'criteria', id });
          }}
          canManage={canManage}
          manageActive={manage}
          onManage={() => setManage(true)}
        />
      </aside>
      <section ref={detailRef} aria-label="Detail" className="min-w-0 scroll-mt-[72px]">
        {manage && canManage ? (
          <ManageCriteria
            engagementId={engagementId}
            groups={view.criteriaGroups}
            onDone={() => setManage(false)}
          />
        ) : criterion ? (
          <CriterionPane
            key={criterion.id}
            engagementId={engagementId}
            criterion={criterion}
            thresholds={readiness.thresholds}
            domains={view.domains}
            onOpenContract={(kind) => {
              void navigate({
                to:
                  kind === 'deviations'
                    ? '/engagements/$engagementId/contract/deviations'
                    : '/engagements/$engagementId/contract/deliverables',
                params: { engagementId },
              });
            }}
          />
        ) : (
          <EmptyState
            title="No go-live criteria yet"
            description="Add the criteria and their weights from Manage criteria and weights."
          />
        )}
      </section>
    </div>
  );
}
